import React, { useState, useRef, useEffect } from 'react';
import { Button, message, Spin, Modal, Input, Radio, Tooltip, Collapse } from 'antd';
import { RedoOutlined, StarOutlined } from '@ant-design/icons';
import ChatAnimationImage from "../../assets/images/icons/chat_circle.gif";
import UserIcon from "../../assets/images/27470347_7309691 1.svg";
import BotIcon from "../../assets/images/1100838311-preview 1.svg";
import { generateAnswer, getPdf, saveFeedback } from '../../apis/ComplianceGptAPI';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import "perfect-scrollbar/css/perfect-scrollbar.css";
import { Auth } from "../../libs/utilities/helpers/Auth";
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const { TextArea } = Input;

const compulsoryFeedbackUsers = [
  '162395', // Neha Solani
  '220121', // P S Sumathi
  '180429', // Nithun Nair
  '129429', // Rizwan Mohammed
  '180194', //Manjiri Gandhi
  '214550', //Nagendra
];
const compulsoryFeedbackDepartments = ['Process Excellence'];
const Chat = ({ prompt, showTour }) => {

  const dispatch = useDispatch();
  const [userInput, setUserInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isWaiting, setIsWaiting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const chatContentRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfData, setPDFDate] = useState({ blob: "", page: 0 });
  const [pdfLoading, setPDFLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState('');
  const [totalFileCount, setTotalFileCount] = useState(0);
  const quillRef = useRef(null);
  const [dislikeModal, setDislikeModal] = useState({ visible: false, messageId: null, starRating: null });
  const [dislikeFeedback, setDislikeFeedback] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const dislikeInputRef = useRef(null);
  const [feedbackRequired, setFeedbackRequired] = useState(false);
  let showUserInput = true;
  const loggedInUser = JSON.parse(localStorage.getItem('AuthUser'));
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [ratings, setRatings] = useState({});
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState({});
  const [visibleObjects, setVisibleObjects] = useState({}); // Tracks how many objects are shown per array
  const [activeKeys, setActiveKeys] = useState({}); // Track active panels
  const [openedBoxes, setOpenedBoxes] = useState({});
  const messagesRef = useRef([]);
  const env = process.env.REACT_APP_STAGE;


  useEffect(() => {
    setUserInput(prompt);
  }, [prompt]);


  useEffect(() => {
    const lastMessage = messagesRef.current[chatHistory.length - 1];
    if (chatContentRef.current && lastMessage) {
      const chatBox = chatContentRef.current;
      const lastMessageRect = lastMessage.getBoundingClientRect();
      const chatBoxRect = chatBox.getBoundingClientRect();
      // Check if last message is not fully visible
      if (lastMessageRect.bottom > chatBoxRect.bottom) {
        lastMessage.scrollIntoView({
          behavior: "smooth",
          block: "start", // Scroll just enough to show the message
        });
      }
    }

  }, [chatHistory]);

  useEffect(() => {
    const handleScroll = () => {
      if (chatContentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = chatContentRef.current;
        setShowScrollButton(scrollTop + clientHeight < scrollHeight - 10);
      }
    };

    const chatDiv = chatContentRef.current;
    if (chatDiv) {
      chatDiv.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatDiv) {
        chatDiv.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);


  useEffect(() => {
    const psContainer = chatContentRef.current;
    if (psContainer ?.scrollHeight > psContainer ?.clientHeight) {
      psContainer ?.classList.add("has-scroll");
    } else {
      psContainer ?.classList.remove("has-scroll");
    }
  }, [chatHistory]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      Auth.setTxnId(uuidv4());
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const isCompulsoryFeedbackUser = () => {
    return compulsoryFeedbackUsers.includes(loggedInUser ?.Username) || compulsoryFeedbackDepartments.includes(loggedInUser ?.Department);
  };

  const handleRefresh = () => {
    setChatHistory([]);
    setUserInput('');
    setIsWaiting(false);
    setSuggestions([]);
    setPDFDate({ blob: "", page: 0 });
    setSelectedFile('');
    Auth.setTxnId(uuidv4());
    setFeedbackGiven({});
    setFeedbackRequired(false);

    if (chatContentRef.current) {
      chatContentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",  // Smooth scrolling to the top
      });
    }
    message.destroy();
    message.success('Chat cleared');
  };

  const handleChatSubmit = async (selectedMessage) => {
    const lastMessage = chatHistory[chatHistory.length - 1];
    const botProcessing = lastMessage ?.isLoading;
    message.destroy();

    const strippedText = selectedMessage.trim();
    const cleanedInput = new DOMParser().parseFromString(strippedText, 'text/html').body.textContent.trim();
    const HTML_TAG_REGEX = /<[^>]+>/;

    if (botProcessing && (env === "STAGING" || env === "DEVELOPMENT")) {
      message.error("Please wait for the response to your previous query.");
      return;
    } else if (!cleanedInput || cleanedInput.length > 300 || HTML_TAG_REGEX.test(cleanedInput)) {
      message.destroy();
      if (!cleanedInput) {
        message.error("Invalid input.");
      } else if (cleanedInput.length > 300) {
        message.error("The prompt must be limited to a maximum of 300 characters.")
      } else if (HTML_TAG_REGEX.test(cleanedInput)) {
        message.error("Please enter a valid prompt without special characters");
      }
      return;
    }
    setIsWaiting(true);
    setUserInput('');
    let uuid = uuidv4();
    let queryData = {
      type: 'user',
      uuid: uuid,
      query: cleanedInput,
      response: null,
      meta: [],
      isLoading: true,
      content: cleanedInput,
      responseType: "success"
    };
    setChatHistory((prevMessages) => [
      ...prevMessages,
      queryData
    ]);
    // scollChatToBottom();

    const requestBody = {
      query: cleanedInput,
      query_id: uuid,
    };

    try {
      const response = await dispatch(generateAnswer(requestBody));
      if (response.error) {
        let errorMessage;
        if (response.payload.statusCode == 'ERR0001') {
          let query_id = response ?.meta.arg.query_id;
          setChatHistory((previousHistory) => {
            let updatedHistory = previousHistory;
            const targetIndex = updatedHistory.findIndex(item => item.uuid === query_id);
            updatedHistory[targetIndex].response = { answer: errorMessage || "Something went wrong!" };
            updatedHistory[targetIndex].meta = [];
            updatedHistory[targetIndex].isLoading = false;
            updatedHistory[targetIndex].responseType = "error";
            updatedHistory[targetIndex].messageId = null;
            return [...updatedHistory];
          });
        }
      }
      else if (response.statusCode == "ERR0045" || response.data == null || response.statusCode == 'ERR0044') {

        setChatHistory((previousHistory) => {
          let updatedHistory = previousHistory;
          const targetIndex = updatedHistory.findIndex(item => item.uuid === response.meta.arg.query_id);
          const limitedResponse = response ?.payload ?.data ?.response ?.slice(0, 3) || [];
          updatedHistory[targetIndex].response = limitedResponse;
          updatedHistory[targetIndex].isLoading = false;
          updatedHistory[targetIndex].responseType = "success";
          updatedHistory[targetIndex].messageId = response ?.payload ?.data.messageId;
          return [...updatedHistory];
        });
      }
      else {
        setChatHistory((previousHistory) => {

          let updatedHistory = previousHistory;
          const targetIndex = updatedHistory.findIndex(item => item.uuid === response.meta.arg.query_id);
          const limitedResponse = response ?.payload ?.data ?.response ?.slice(0, 3) || response ?.payload ?.data ?.response;
          updatedHistory[targetIndex].response = limitedResponse;
          // updatedHistory[targetIndex].meta = response.payload?.data?.response.map(item => ({ fileId: item.file_id, name: getFileName(item.file_id), page: item.page }));
          updatedHistory[targetIndex].isLoading = false;
          updatedHistory[targetIndex].responseType = "success";
          updatedHistory[targetIndex].messageId = response ?.payload ?.data.messageId;
          return [...updatedHistory];
        });

        if (isCompulsoryFeedbackUser()) {
          setFeedbackRequired(true);
        }
      }
    } catch (error) {
      // console.error(error);
    } finally {
      setIsWaiting(false);
      // scollChatToBottom();
    }
  };

  const handleStarClick = (starRating, messageId) => {
    if (feedbackGiven[messageId] ?.starRating >= 4) {
      return; // Prevent editing for 4-5 star feedback
    }

    setRatings((prev) => ({ ...prev, [messageId]: starRating }));

    if (starRating <= 3) {
      const existingFeedback = feedbackGiven[messageId];
      setDislikeModal({
        visible: true,
        messageId,
        starRating,
      });

      // Populate modal with existing feedback
      if (existingFeedback) {
        setSelectedOption(existingFeedback.reason || null);
        setDislikeFeedback(existingFeedback.comments || '');
      } else {
        setSelectedOption(null);
        setDislikeFeedback('');
      }
    } else {
      // Save feedback directly for ratings 4 and 5
      const feedbackData = { messageId, feedbackType: starRating };
      dispatch(saveFeedback(feedbackData));
      setFeedbackGiven((prev) => ({
        ...prev,
        [messageId]: { feedbackGiven },
      }));
      message.success('Feedback submitted successfully!', 3);
    }
  };

  const handleKeyPress = (event) => {
    const cleanedInput = new DOMParser().parseFromString(userInput, 'text/html').body.textContent.trim();
    const lastMessage = chatHistory[chatHistory.length - 1];

    // Prevent new input if the bot is still processing or feedback is pending
    const botProcessing = lastMessage ?.isLoading;


    // Handle input submission on Enter
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      message.destroy();

      if (!botProcessing && (env === "STAGING" || env === "DEVELOPMENT")) {
        handleChatSubmit(cleanedInput);
        return;
      } else if (cleanedInput && cleanedInput.length <= 300) {
        handleChatSubmit(cleanedInput);
      } else {
        message.destroy();
        message.error("Invalid input. Ensure it is under 300 characters.");
      }
    }
  };

  // Insert newline in the Quill editor on Shift+Enter
  const insertNewLine = () => {
    const quill = quillRef.current.getEditor();
    const selection = quill.getSelection();
    quill.insertText(selection.index, '\n');
  };

  const handleInputChange = async (event) => {
    const value = event.target.value;
    if (showUserInput) {

      setUserInput(value);
      // setUserInput(quillRef.current.input.value);
      const strippedText = new DOMParser().parseFromString(value, 'text/html').body.textContent.trim().toLowerCase();
      ;

      // Split input into words
      const words = strippedText.split(/\s+/);

    } else {
      showUserInput = true;
    }
  };

  const handleDislikeInputChange = (e) => {
    setDislikeFeedback(e.target.value);
  };

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const handleModalClose = () => {
    const existingFeedback = feedbackGiven[dislikeModal.messageId];

    if (existingFeedback) {
      // Restore previous feedback (if any)
      setRatings((prev) => ({
        ...prev,
        [dislikeModal.messageId]: existingFeedback.starRating,
      }));
      setSelectedOption(existingFeedback.reason || null);
      setDislikeFeedback(existingFeedback.comments || '');
    } else {
      // Reset the star rating to undefined (if no existing feedback)
      setRatings((prev) => ({
        ...prev,
        [dislikeModal.messageId]: undefined,
      }));
      setSelectedOption(null);
      setDislikeFeedback('');
    }

    // Close the modal
    setDislikeModal({ visible: false, messageId: null, starRating: null });
  };


  const handleModalSubmit = async () => {
    if (!selectedOption) {
      message.error('Please select a reason for your dislike.');
      return;
    }

    const feedbackMessage = `${selectedOption}${dislikeFeedback ? `. ${dislikeFeedback}` : ''}`;

    const feedbackData = {
      messageId: dislikeModal.messageId,
      feedbackType: dislikeModal.starRating,
      feedback: feedbackMessage,
    };

    await dispatch(saveFeedback(feedbackData));
    setFeedbackGiven((prev) => ({
      ...prev,
      [dislikeModal.messageId]: {
        starRating: dislikeModal.starRating,
        reason: selectedOption,
        comments: dislikeFeedback,
      },
    }));

    message.success('Feedback updated successfully!', 3);

    // Reset modal state
    setDislikeModal({ visible: false, messageId: null });
    setSelectedOption(null);
    setDislikeFeedback('');
  };


  useEffect(() => {
    if (dislikeModal.visible) {
      const existingFeedback = feedbackGiven[dislikeModal.messageId];

      if (existingFeedback) {
        setSelectedOption(existingFeedback.reason || null);
        setDislikeFeedback(existingFeedback.comments || '');
      }
    }
  }, [dislikeModal]);

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const openPdf = async (args) => {
    //console.log(args, "args");
    setSelectedFile(args.file_name);
    const filePath = args.file_path;
    const trimmedPath = filePath.substring(0, filePath.lastIndexOf('/'));
    const data = {
      fileName: trimmedPath,
    };
    setPDFLoading(true);
    try {
      const response = await dispatch(getPdf(data));
      if (!response.error) {
        const binaryString = window.atob(response.payload.data);

        const binary = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          binary[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([binary], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const pdfViewerUrl = `${url}#page=${args.page_number}`;
        window.open(pdfViewerUrl, "_blank");
        if (blob) {
          setPDFLoading(false);
        }
      } else {
        setPDFLoading(false);
      }
    } catch (err) {

    }

  }

  const truncateText = (text, wordLimit) => {
    return text.split(" ").slice(0, wordLimit).join(" ") + (text.split(" ").length > wordLimit ? "..." : "");
  };


  const formatText = (text) => {

    // let htmlText = text.replace(/^(\n\n)+|(\n\n)+$/g, ''); 
    // htmlText = text.replace(/\n/g, '<br>');
    // // htmlText = text.replace(/^[\s<br>]+/, '');
    // htmlText = htmlText.replace(/^\s+/, '');
    // htmlText = htmlText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // htmlText = htmlText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // htmlText = htmlText.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');

    // htmlText = htmlText.replace(/(?<=\.\d{1,}\.<br>)?(?<!^)(?=\d+\.\d+)/g, '<br>');
    // htmlText = htmlText.replace(/(?<!^)(?<!<br>)(?=\d+\.\d+)/g, '<br><br>');
    // return htmlText;
  };



  return (
    <>
      {/* <PDFViewer pdfData={pdfData} show={isModalOpen} handleClose={() => setIsModalOpen(false)} /> */}
      <>
        {chatHistory.length === 0 ? (
          <div className="chatbot-content" ref={chatContentRef} style={{ justifyContent: "center" }}>
            <img className='animation' src={ChatAnimationImage} alt="waiting-gif" width={100} height={100} />
            <p className='text5'>
              Ask Compliance GPT about regulatory notifications<br /> and get clear,concise answers in simple language.
            </p>
            <p className='text7'>
              <b className="text8">The responses are AI generated hence you must verify the information from the relevant notification. In case of any query<br />connect with compliance team.</b></p>
          </div>
        ) :

          <div className="chatbot-content-compliance compliance-gpt" ref={chatContentRef}>
            {/* {showScrollButton && (
              <button
                className="scroll-to-bottom-btn"
                onClick={scollChatToBottom}
              >
                <ArrowDownOutlined />
              </button>
            )} */}
            {chatHistory.length > 0 &&
              chatHistory.map((item, index) =>
                (
                  <div key={index} ref={(el) => (messagesRef.current[index] = el)} style={{ width: "100%", display: "flex", flexDirection: "column" }}>

                    <div className={`chat-message user-compliance`} style={{ alignSelf: "flex-end" }}>
                      <span className='user-message-compliance success'>{item.query}</span>
                      <img className='user-icon-compliance' src={UserIcon} alt="user-icon" width={40} height={40} />
                    </div>

                    <div className={`chat-message-compliance bot`}>
                      <img className='bot-icon-compliance' src={BotIcon} alt="bot-icon" width={38} height={38} />
                      {item.isLoading ? (
                        <Spin size="small" style={{ marginLeft: 8, display: "flex", alignItems: "center" }} />
                      ) : (
                          <div >
                            <div className={`bot-message-compliance ${item.responseType}`}>
                              <div className="chatbot-container">
                                {item.response.length == 0 ?
                                  'No Data found' :
                                  <>
                                    {
                                      item.response.answer ?
                                        <>{item.response.answer}</> :
                                        item.response.map((responseArray, responseIndex) => {
                                          const arrayKey = `${index}-${responseIndex}`;
                                          const defaultVisibleCount = 4;
                                          const visibleCount = visibleObjects[arrayKey] || defaultVisibleCount;
                                          const isOpened = responseIndex === 0 || activeKeys[arrayKey];

                                          return (
                                            <div key={arrayKey} className="response-group">
                                              {
                                                responseArray[0].summary ? <>
                                                  {isOpened && (
                                                    <>
                                                      <div className="first-heading">
                                                        <p>{truncateText(responseArray[0] ?.summary, 20)}</p>
                                                      </div>
                                                      <Collapse accordion ghost activeKey={activeKeys[arrayKey] || (responseIndex === 0 ? ["panel-0"] : [])}
                                                        onChange={(keys) => setActiveKeys({ ...activeKeys, [arrayKey]: keys })}>
                                                        {responseArray.slice(0, visibleCount).map((response, resIndex) => {
                                                          const monthIndex = parseInt(response.month, 10) - 1;
                                                          const monthName = monthNames[monthIndex] || "Unknown";
                                                          const isPanelOpen = activeKeys[arrayKey] ?.includes(`panel-${resIndex}`);
                                                          return (
                                                            <Collapse.Panel key={`panel-${resIndex}`}
                                                              header={
                                                                <div className="heading-accord">
                                                                  <div>
                                                                    <strong>{monthName}-{response.year}:</strong> {response.file_name}
                                                                  </div>
                                                                </div>
                                                              }
                                                            >
                                                              {/* <p className="accord-content" dangerouslySetInnerHTML={{ __html: formatText(response.content) }} /> */}
                                                              <Markdown
                                                                remarkPlugins={[remarkGfm]}
                                                                components={{
                                                                  h3: ({ node, ...props }) => <h3 style={{ fontSize: "14px" }} {...props} />
                                                                }}
                                                              >{(response.content)}</Markdown>

                                                              <div style={{ color: "gray", fontSize: "12px", paddingTop: "4px", marginTop: "4px" }}>This response is generated by Gen AI. Please verify the information by clicking on the given page link.</div>
                                                              <div className="metatext">
                                                                <div style={{ display: "flex", gap: "5px" }}>
                                                                  <div className="response-text-page" onClick={() => openPdf(response)} ><Tooltip placement="top" title="View Pdf">Page No: <a>{response.page_number}</a></Tooltip></div>
                                                                  {(selectedFile === response ?.file_name) && pdfLoading && <span style={{ fontSize: "12px", color: "#ED1C24" }}>Loading...</span>}
                                                                </div>
                                                                {/* <div className="" ><Tooltip placement="top" title="">Page No: <a>{responseArray[0].page_number}</a></Tooltip></div> */}
                                                                <div className="response-text-score">Relevance Score: <span>{Math.round(response.score)}%</span></div>
                                                              </div>
                                                            </Collapse.Panel>
                                                          )
                                                        })}
                                                      </Collapse>

                                                      {responseArray.length > defaultVisibleCount && (
                                                        <div>
                                                          {visibleCount < responseArray.length ? (
                                                            <Button
                                                              // type="link"
                                                              className="show-more"
                                                              onClick={() => setVisibleObjects({ ...visibleObjects, [arrayKey]: visibleCount + 3 })}
                                                            >
                                                              Show More
                                                </Button>
                                                          ) : (
                                                              <Button
                                                                // type="link"
                                                                className="show-more"
                                                                onClick={() => setVisibleObjects({ ...visibleObjects, [arrayKey]: defaultVisibleCount })}
                                                              >
                                                                Show Less
                                                  </Button>
                                                            )}
                                                        </div>
                                                      )}
                                                    </>
                                                  )}
                                                </> :
                                                  // <p className="accord-content" dangerouslySetInnerHTML={{ __html: formatText(responseArray[0] ?.content) }} />
                                                  <Markdown
                                                    remarkPlugins={[remarkGfm]}
                                                    components={{
                                                      h3: ({ node, ...props }) => <h3 style={{ fontSize: "14px" }} {...props} />
                                                    }}
                                                  >{(responseArray[0] ?.content)}</Markdown>


                                              }
                                            </div>
                                          );
                                        })
                                    }
                                  </>

                                }


                              </div>
                            </div>


                            {item.response.length > 1 && <p className="other-response-heading">Here are additional relevant responses that may assist you</p>}

                            {item.response.length > 1 && item.response.slice(1).map((responseArray, responseIndex) => {
                              const arrayKey = `other-${index}-${responseIndex}`;
                              const isBoxOpen = openedBoxes[arrayKey] || false;
                              const defaultVisibleCount = 2;
                              const visibleCount = visibleObjects[arrayKey] || defaultVisibleCount;
                              const isFirstPanel = responseIndex === 0;
                              const currentlyActiveKey = activeKeys[arrayKey] || (isFirstPanel ? ["panel-0"] : []);
                              return (
                                <div key={arrayKey} className="additional-response-container">
                                  <div
                                    className="additional-response-heading"
                                    style={{ marginTop: "10px", display: "flex" }}
                                    onClick={() => setOpenedBoxes({ ...openedBoxes, [arrayKey]: !isBoxOpen })}
                                  >
                                    <div>
                                      <p>{truncateText(responseArray[0] ?.summary, 20)}</p>
                                    </div>
                                    <div>
                                      <span>{isBoxOpen ? " ▼" : " ▶"}</span>
                                    </div>
                                  </div>

                                  {isBoxOpen && (
                                    <div className={`bot-message-compliance ${item.responseType}`} style={{ marginTop: "10px" }}>
                                      <div className="chatbot-container response-group-other">
                                        <Collapse
                                          accordion
                                          ghost
                                          activeKey={currentlyActiveKey}
                                          onChange={(keys) => setActiveKeys({ ...activeKeys, [arrayKey]: keys })}
                                        >
                                          {responseArray.slice(0, visibleCount).map((response, resIndex) => {
                                            const monthIndex = parseInt(response.month, 10) - 1;
                                            const monthName = monthNames[monthIndex] || "Unknown";
                                            const isPanelOpen = currentlyActiveKey.includes(`panel-${resIndex}`);
                                            return (
                                              <Collapse.Panel
                                                key={`panel-${resIndex}`}
                                                header={
                                                  <div className="heading-accord">
                                                    <div>
                                                      <strong>{monthName}-{response.year}:</strong> {response.file_name}
                                                    </div>
                                                  </div>
                                                }
                                              >
                                                {/* <p className="accord-content" dangerouslySetInnerHTML={{ __html: formatText(response.content) }} /> */}
                                                <Markdown
                                                  remarkPlugins={[remarkGfm]}
                                                  components={{
                                                    h3: ({ node, ...props }) => <h3 style={{ fontSize: "14px" }} {...props} />
                                                  }}
                                                >{(response.content)}</Markdown>
                                                <div style={{ color: "gray", fontSize: "12px", paddingTop: "4px", marginTop: "4px" }}>This response is generated by Gen AI. Please verify the information by clicking on the given page link.</div>
                                                <div className="metatext">
                                                  <div style={{ display: "flex", gap: "5px" }}>
                                                    <div className="response-text-page" onClick={() => openPdf(response)} ><Tooltip placement="top" title="View Pdf">Page No: <a>{response.page_number}</a></Tooltip></div>
                                                    {(selectedFile === response ?.file_name) && pdfLoading && <span style={{ fontSize: "12px", color: "#ED1C24" }}>Loading...</span>}
                                                  </div>
                                                  <div className="response-text-score">Relevance Score: <span>{Math.round(response.score)}%</span></div>
                                                </div>
                                              </Collapse.Panel>
                                            );
                                          })}
                                        </Collapse>
                                        {responseArray.length > defaultVisibleCount && (
                                          <div>
                                            {visibleCount < responseArray.length ? (
                                              <Button
                                                className="show-more"
                                                onClick={() =>
                                                  setVisibleObjects({ ...visibleObjects, [arrayKey]: visibleCount + 3 })
                                                }
                                              >
                                                Show More
                                              </Button>
                                            ) : (
                                                <Button
                                                  className="show-more"
                                                  onClick={() =>
                                                    setVisibleObjects({ ...visibleObjects, [arrayKey]: defaultVisibleCount })
                                                  }
                                                >
                                                  Show Less
                                                </Button>
                                              )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}



                            {item.responseType !== 'error' && (
                              <div className="compliance-star-rating" style={{ display: 'flex', marginLeft: "7px" }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <span
                                    key={star}
                                    onClick={() => ratings[item.messageId] >= 4 ? null : handleStarClick(star, item.messageId)}
                                    className={`star ${ratings[item.messageId] >= star ? "filled" : ""}`}
                                    style={{
                                      cursor: ratings[item.messageId] >= 4 ? "not-allowed" : "pointer" // Show blocked cursor for 4-5 ratings
                                    }}
                                  >
                                    <StarOutlined />
                                  </span>
                                ))}
                              </div>
                            )}


                          </div>
                        )}
                    </div>

                  </div>

                )

              )
            }
          </div>
        }
      </>
      <div className="chat-input-compliance">
        <div className="main-chat">
          <div className="quill-container-compliance">
            <Input
              placeholder="Type your prompt..."
              ref={quillRef}
              className="quill-prompt-compliance"
              theme="bubble"
              modules={{ toolbar: false }}
              value={userInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}

            />

            <Tooltip title="Clear Chat">
              <Button
                className="refresh-button-compliance"
                type="default"
                onClick={handleRefresh}
              >
                <RedoOutlined />
              </Button>
            </Tooltip>

          </div>

          <Button
            className="chat-input-button"
            type="primary"
            onClick={() => handleChatSubmit(userInput)}
            disabled={isWaiting || userInput.length == 0 || userInput === "<p><br></p>"}
          >
            Submit
          </Button>
        </div>
        <div className="footer-text">
          Compliance GPT is prone to factual inaccuracies; It is advisable to verify the information.
        </div>
      </div>



      <Modal
        title="Tell us more, why did you dislike?"
        open={dislikeModal.visible}
        onCancel={handleModalClose}
        onOk={handleModalSubmit}
        okText="Submit"
        closable={false}
        maskClosable={false}
        okButtonProps={{
          disabled: !selectedOption
        }}
      >
        <Radio.Group onChange={handleOptionChange} value={selectedOption}>
          <Radio value="Relevant but not exhaustive">Relevant but not exhaustive</Radio>
          <Radio value="Slightly irrelevant">Slightly irrelevant </Radio>
          <Radio value="Completely irrelevant">Completely irrelevant</Radio>

        </Radio.Group>
        <TextArea
          ref={dislikeInputRef}
          placeholder="Please provide additional comments (optional)"
          value={dislikeFeedback}
          onChange={handleDislikeInputChange}
          autoFocus
          style={{
            height: "6rem",
            borderRadius: "5px",
            padding: "0.5rem",
            fontSize: "14px",
            boxSizing: "border-box",
            lineHeight: "1.25",
            verticalAlign: "top",
            display: "block",
            width: "100%",
            resize: "none"
          }}
          maxLength={1000}
        />
      </Modal>

    </ >
  );
};

export default Chat;